import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { fr, enUS, it } from 'date-fns/locale';
import { Clock, Users, ArrowLeft, Plus, Minus, Check, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PaymentIcons } from '../components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TripDetailPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { t, language, getLocalizedField } = useLanguage();
  
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [isPrivatization, setIsPrivatization] = useState(false);
  const [participants, setParticipants] = useState([
    { first_name: '', last_name: '', age: '', phone: '' }
  ]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [processing, setProcessing] = useState(false);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);
  const [squarePayments, setSquarePayments] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);

  const locales = { fr, en: enUS, it };

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        const response = await axios.get(`${API}/trips/${tripId}`);
        setTrip(response.data);
        // Set default duration if trip has duration options
        if (response.data.duration_options && response.data.duration_options.length > 0) {
          setSelectedDuration(response.data.duration_options[0]);
        }
      } catch (error) {
        console.error('Error fetching trip:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();
  }, [tripId, t]);

  // Fetch blocked dates for this trip
  useEffect(() => {
    const fetchBlocked = async () => {
      try {
        const r = await axios.get(`${API}/trips/${tripId}/blocked-dates`);
        setBlockedDates(r.data);
      } catch (e) {}
    };
    fetchBlocked();
  }, [tripId]);

  // Load Square SDK (only creates payments instance, does NOT attach card yet)
  useEffect(() => {
    const loadSquare = async () => {
      try {
        const configRes = await axios.get(`${API}/config/square`);
        const { application_id } = configRes.data;
        
        if (!application_id) {
          console.warn('Square not configured');
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://web.squarecdn.com/v1/square.js';
        script.async = true;
        script.onload = async () => {
          if (!window.Square) return;
          try {
            const payments = window.Square.payments(application_id);
            setSquarePayments(payments);
          } catch (err) {
            console.error('Square payments init error:', err);
          }
        };
        document.body.appendChild(script);
      } catch (error) {
        console.error('Error loading Square:', error);
      }
    };
    loadSquare();

    return () => {
      const script = document.querySelector('script[src*="square.js"]');
      if (script) script.remove();
    };
  }, []);

  // Reset card when slot changes (date change or slot switch requires fresh card)
  const prevSlotRef = React.useRef(null);
  useEffect(() => {
    if (prevSlotRef.current && prevSlotRef.current !== selectedSlot) {
      // Slot changed - destroy old card
      if (card) {
        try { card.destroy(); } catch (e) { /* already detached */ }
      }
      setCard(null);
      setSquareLoaded(false);
    }
    if (!selectedSlot) {
      if (card) {
        try { card.destroy(); } catch (e) { /* already detached */ }
      }
      setCard(null);
      setSquareLoaded(false);
    }
    prevSlotRef.current = selectedSlot;
  }, [selectedSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach card when #card-container becomes visible (selectedSlot is set)
  useEffect(() => {
    if (!squarePayments || !selectedSlot || card) return;
    
    const attachCard = async () => {
      try {
        // Small delay to ensure DOM is rendered
        await new Promise(r => setTimeout(r, 300));
        const container = document.getElementById('card-container');
        if (!container) return;
        
        const cardInstance = await squarePayments.card();
        await cardInstance.attach('#card-container');
        setCard(cardInstance);
        setSquareLoaded(true);
      } catch (err) {
        console.error('Square card attach error:', err);
      }
    };
    attachCard();
  }, [squarePayments, selectedSlot, card]);

  const fetchAvailability = useCallback(async (date) => {
    if (!date) return;
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/trips/${tripId}/availability?date=${dateStr}`);
      setAvailability(response.data);
      // Auto-select first available slot
      const firstAvailable = response.data.find(s => s.is_available);
      if (firstAvailable) {
        setSelectedSlot(firstAvailable.time_slot);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  }, [tripId]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate, fetchAvailability]);

  const addParticipant = () => {
    const maxFromTrip = trip?.max_participants || 4;
    // Limit by available spots if we have slot info
    const slotInfo = selectedSlot ? availability.find(a => a.time_slot === selectedSlot) : null;
    const maxAllowed = slotInfo ? Math.min(maxFromTrip, slotInfo.available_spots) : maxFromTrip;
    if (participants.length < maxAllowed) {
      setParticipants([...participants, { first_name: '', last_name: '', age: '', phone: '' }]);
    }
  };

  const removeParticipant = (index) => {
    if (participants.length > 1) {
      setParticipants(participants.filter((_, i) => i !== index));
    }
  };

  const updateParticipant = (index, field, value) => {
    const updated = [...participants];
    updated[index][field] = value;
    setParticipants(updated);
  };

  const calculateTotal = () => {
    if (!trip) return 0;
    // Use selected duration prices if available
    const pricePerPerson = selectedDuration ? selectedDuration.price_per_person : trip.price_per_person;
    const privatizationPrice = selectedDuration ? selectedDuration.privatization_price : trip.privatization_price;
    
    if (isPrivatization) return privatizationPrice;
    return pricePerPerson * participants.length;
  };

  const getCurrentPrices = () => {
    if (selectedDuration) {
      return {
        pricePerPerson: selectedDuration.price_per_person,
        privatizationPrice: selectedDuration.privatization_price,
        duration: selectedDuration.duration_hours
      };
    }
    return {
      pricePerPerson: trip?.price_per_person || 0,
      privatizationPrice: trip?.privatization_price || 0,
      duration: trip?.duration_hours || 0
    };
  };

  const validateForm = () => {
    if (!selectedDate || !selectedSlot) {
      toast.error(t('booking.selectDate'));
      return false;
    }
    if (trip?.duration_options && trip.duration_options.length > 0 && !selectedDuration) {
      toast.error(language === 'fr' ? 'Veuillez choisir une durée' : language === 'it' ? 'Seleziona una durata' : 'Please select a duration');
      return false;
    }
    if (!customerEmail || !customerPhone) {
      toast.error(language === 'fr' ? 'Veuillez renseigner votre email et téléphone' : language === 'it' ? 'Inserisci email e telefono' : 'Email & phone required');
      const el = document.getElementById('customer-info-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    for (const p of participants) {
      if (!p.first_name || !p.last_name) {
        toast.error(language === 'fr' ? 'Veuillez renseigner le nom et prénom de chaque participant' : language === 'it' ? 'Inserisci nome e cognome di ogni partecipante' : 'Please fill first and last name for each participant');
        const el = document.getElementById('participants-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }
    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;
    if (!card || !squarePayments) {
      toast.error(language === 'fr' ? 'Système de paiement non prêt, veuillez recharger la page' : 'Payment system not ready');
      return;
    }

    setProcessing(true);
    try {
      const tokenResult = await card.tokenize();
      
      if (tokenResult.status !== 'OK') {
        toast.error(language === 'fr' ? 'Carte invalide, veuillez vérifier vos informations' : 'Card validation failed');
        setProcessing(false);
        return;
      }

      // 3D Secure / SCA verification (required for European cards)
      let verificationToken = null;
      try {
        const totalAmount = calculateTotal();
        const verificationResult = await squarePayments.verifyBuyer(tokenResult.token, {
          amount: totalAmount.toFixed(2),
          currencyCode: 'EUR',
          intent: 'CHARGE',
          billingContact: {
            givenName: participants[0]?.first_name || '',
            familyName: participants[0]?.last_name || '',
            email: customerEmail,
            phone: customerPhone
          }
        });
        verificationToken = verificationResult?.token || null;
      } catch (scaErr) {
        console.error('SCA verification error:', scaErr);
        // SCA verification failed or was cancelled by user
        toast.error(language === 'fr' 
          ? 'La vérification bancaire a échoué ou a été annulée. Veuillez réessayer.' 
          : 'Bank verification failed or was cancelled. Please try again.');
        setProcessing(false);
        return;
      }

      if (!verificationToken) {
        toast.error(language === 'fr'
          ? 'La vérification bancaire est requise. Veuillez réessayer.'
          : 'Bank verification is required. Please try again.');
        setProcessing(false);
        return;
      }

      const bookingData = {
        trip_id: tripId,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedSlot,
        participants: participants.map(p => ({
          first_name: p.first_name,
          last_name: p.last_name,
          age: p.age ? parseInt(p.age) : null,
          phone: p.phone || ""
        })),
        is_privatization: isPrivatization,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        total_amount: calculateTotal(),
        language,
        source_token: tokenResult.token,
        verification_token: verificationToken,
        selected_duration: selectedDuration ? selectedDuration.duration_hours : null
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      
      toast.success(t('booking.success'));
      toast.info(t('booking.confirmationSent'));
      
      navigate(`/booking-confirmation/${response.data.id}`);
    } catch (error) {
      console.error('Booking error:', error);
      const detail = error.response?.data?.detail || '';
      // User-friendly error messages
      let msg;
      if (detail.includes('VERIFICATION_REQUIRED')) {
        msg = language === 'fr' ? 'Votre banque requiert une vérification. Veuillez réessayer.' : 'Your bank requires verification. Please try again.';
      } else if (detail.includes('INSUFFICIENT_FUNDS')) {
        msg = language === 'fr' ? 'Fonds insuffisants sur la carte.' : 'Insufficient funds.';
      } else if (detail.includes('CARD_DECLINED') || detail.includes('GENERIC_DECLINE')) {
        msg = language === 'fr' ? 'Carte refusée. Veuillez essayer une autre carte.' : 'Card declined. Please try another card.';
      } else if (detail.includes('INVALID_EXPIRATION')) {
        msg = language === 'fr' ? 'Date d\'expiration invalide.' : 'Invalid expiration date.';
      } else if (detail.includes('CVV')) {
        msg = language === 'fr' ? 'Code CVV incorrect.' : 'Incorrect CVV.';
      } else if (detail.includes('Not enough available spots')) {
        msg = language === 'fr' ? 'Plus de places disponibles pour ce créneau.' : 'No more spots available.';
      } else {
        msg = language === 'fr' ? 'Erreur de paiement. Veuillez réessayer ou utiliser une autre carte.' : 'Payment error. Please try again or use another card.';
      }
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <p className="text-[#4B5563]">Trip not found</p>
      </div>
    );
  }

  const slotAvailability = availability.find(a => a.time_slot === selectedSlot);

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="trip-detail-page">
      {/* Header */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img
          src={trip.image_url}
          alt={getLocalizedField(trip, 'name')}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-white mb-4 hover:bg-white/10"
            onClick={() => navigate('/trips')}
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('common.back')}
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Playfair_Display']">
            {getLocalizedField(trip, 'name')}
          </h1>
          <div className="flex flex-wrap gap-4 mt-4">
            <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm">
              <Clock size={16} className="inline mr-2" />
              {trip.duration_options && trip.duration_options.length > 0
                ? trip.duration_options.map(o => o.label || `${o.duration_hours}H`).join(' / ')
                : `${trip.duration_hours}h`}
            </span>
            <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm">
              <Users size={16} className="inline mr-2" />
              Max {trip.max_participants}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Left: Details & Form */}
          <div className="lg:col-span-3 space-y-8">
            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-[#000000] font-['Playfair_Display'] mb-4">
                Description
              </h2>
              <p className="text-[#4B5563] leading-relaxed">
                {getLocalizedField(trip, 'description')}
              </p>
            </div>

            {/* Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-['Playfair_Display']">{t('booking.selectDate')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={locales[language]}
                  disabled={(date) => {
                    if (isBefore(date, startOfDay(new Date())) || isBefore(date, addDays(new Date(), -1))) return true;
                    const dateStr = format(date, 'yyyy-MM-dd');
                    return blockedDates.includes(dateStr);
                  }}
                  className="rounded-xl border-0 w-full"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full",
                    month: "space-y-6 w-full",
                    caption: "flex justify-center pt-2 relative items-center mb-2",
                    caption_label: "text-lg font-semibold text-[#000000] font-['Playfair_Display'] capitalize",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-9 w-9 bg-transparent border-2 border-[#000000] rounded-full p-0 opacity-100 hover:bg-[#000000] hover:text-[#b7ede0] transition-all duration-200 inline-flex items-center justify-center",
                    nav_button_previous: "absolute left-2",
                    nav_button_next: "absolute right-2",
                    table: "w-full border-collapse",
                    head_row: "flex w-full justify-around",
                    head_cell: "text-[#000000] font-bold w-12 text-xs uppercase tracking-wider pb-3",
                    row: "flex w-full mt-1 justify-around",
                    cell: "relative p-0.5 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:rounded-xl",
                    day: "h-11 w-11 p-0 font-medium rounded-xl hover:bg-[#e8f8f4] hover:text-[#000000] transition-all duration-200 inline-flex items-center justify-center",
                    day_range_start: "day-range-start",
                    day_range_end: "day-range-end",
                    day_selected: "bg-[#000000] text-[#b7ede0] hover:bg-[#000000] hover:text-[#b7ede0] focus:bg-[#000000] focus:text-[#b7ede0] font-bold shadow-lg shadow-black/20",
                    day_today: "bg-[#b7ede0] text-[#000000] font-bold",
                    day_outside: "text-gray-300 opacity-30",
                    day_disabled: "text-gray-300 opacity-30",
                    day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                    day_hidden: "invisible",
                  }}
                  data-testid="booking-calendar"
                />
              </CardContent>
            </Card>

            {/* Duration Selector (for trips with multiple durations like Pêche au Gros) */}
            {selectedDate && trip.duration_options && trip.duration_options.length > 0 && (
              <Card className="animate-fade-up">
                <CardHeader>
                  <CardTitle className="text-xl font-['Playfair_Display']">
                    {language === 'fr' ? 'Choisissez votre durée' : language === 'it' ? 'Scegli la durata' : 'Choose your duration'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {trip.duration_options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedDuration(option);
                          setSelectedSlot(null);
                        }}
                        className={`relative p-5 rounded-xl border-2 transition-all duration-200 text-left ${
                          selectedDuration?.label === option.label
                            ? 'border-[#b7ede0] bg-[#b7ede0]/10 shadow-md'
                            : 'border-[#E2E8F0] hover:border-[#b7ede0]/50 hover:bg-[#F8FAFC]'
                        }`}
                        data-testid={`duration-option-${option.label}`}
                      >
                        {selectedDuration?.label === option.label && (
                          <div className="absolute top-3 right-3 w-6 h-6 bg-[#b7ede0] rounded-full flex items-center justify-center">
                            <Check size={14} className="text-[#000000]" />
                          </div>
                        )}
                        <div className="text-2xl font-bold text-[#000000] mb-1">{option.label}</div>
                        <div className="text-lg font-semibold text-[#000000]">{option.price_per_person}€ <span className="text-sm font-normal text-[#4B5563]">/ pers.</span></div>
                        <div className="text-sm text-[#4B5563] mt-1">
                          {language === 'fr' ? 'Privatisation' : language === 'it' ? 'Privatizzazione' : 'Privatization'}: {option.privatization_price}€
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Time Slots */}
            {selectedDate && (!trip.duration_options || trip.duration_options.length === 0 || selectedDuration) && (
              <Card className="animate-fade-up">
                <CardHeader>
                  <CardTitle className="text-xl font-['Playfair_Display']">{t('booking.selectSlot')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {trip.time_slots
                      .filter(slot => {
                        if (!trip.duration_options || trip.duration_options.length === 0 || !selectedDuration) return true;
                        return slot.label.includes(`(${selectedDuration.label})`);
                      })
                      .map((slot, index) => {
                        const slotInfo = availability.find(a => a.time_slot === slot.label);
                        const isAvailable = slotInfo?.is_available ?? true;
                        const spots = slotInfo?.available_spots ?? trip.max_participants;
                      
                        return (
                          <button
                            key={index}
                            onClick={() => isAvailable && setSelectedSlot(slot.label)}
                            disabled={!isAvailable}
                            className={`time-slot-badge justify-between p-4 ${
                              selectedSlot === slot.label ? 'selected' : ''
                            } ${!isAvailable ? 'disabled' : ''}`}
                            data-testid={`time-slot-${index}`}
                          >
                            <span className="font-medium">{slot.label}</span>
                            <span className={`text-sm ${isAvailable ? 'text-[#10B981]' : 'text-[#FF3B30]'}`}>
                              {isAvailable ? `${spots} ${t('trips.spotsAvailable')}` : t('trips.fullyBooked')}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Participants */}
            {selectedSlot && (
              <Card className="animate-fade-up" id="participants-section">
                <CardHeader>
                  <CardTitle className="text-xl font-['Playfair_Display'] flex items-center justify-between">
                    {t('booking.participants')}
                    {(() => {
                      const slotInfo = availability.find(a => a.time_slot === selectedSlot);
                      const spots = slotInfo?.available_spots ?? trip.max_participants;
                      return (
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${spots <= 1 ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : spots <= 2 ? 'bg-orange-100 text-orange-700' : 'bg-[#b7ede0]/20 text-[#000000]'}`}>
                          {spots} {language === 'fr' ? (spots > 1 ? 'places restantes' : 'place restante') : (spots > 1 ? 'spots left' : 'spot left')}
                        </span>
                      );
                    })()}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Privatization Toggle */}
                  <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl">
                    <div>
                      <p className="font-medium text-[#000000]">{t('booking.privatize')}</p>
                      <p className="text-sm text-[#4B5563]">{t('booking.privatizeDesc')}</p>
                    </div>
                    <Switch
                      checked={isPrivatization}
                      onCheckedChange={setIsPrivatization}
                      data-testid="privatization-switch"
                    />
                  </div>

                  {/* Participant Forms */}
                  {participants.map((participant, index) => (
                    <div key={index} className="participant-form animate-fade-up">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-[#000000]">Participant {index + 1}</h4>
                        {participants.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParticipant(index)}
                            className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-red-50"
                          >
                            <Minus size={16} className="mr-1" />
                            {t('booking.removeParticipant')}
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>{t('booking.firstName')} *</Label>
                          <Input
                            value={participant.first_name}
                            onChange={(e) => updateParticipant(index, 'first_name', e.target.value)}
                            placeholder="Jean"
                            data-testid={`participant-${index}-firstname`}
                          />
                        </div>
                        <div>
                          <Label>{t('booking.lastName')} *</Label>
                          <Input
                            value={participant.last_name}
                            onChange={(e) => updateParticipant(index, 'last_name', e.target.value)}
                            placeholder="Dupont"
                            data-testid={`participant-${index}-lastname`}
                          />
                        </div>
                        <div>
                          <Label>{t('booking.age')}</Label>
                          <Input
                            type="number"
                            value={participant.age}
                            onChange={(e) => updateParticipant(index, 'age', e.target.value)}
                            placeholder="25"
                            min="1"
                            max="120"
                            data-testid={`participant-${index}-age`}
                          />
                        </div>
                        <div>
                          <Label>{t('booking.phone')}</Label>
                          <Input
                            type="tel"
                            value={participant.phone}
                            onChange={(e) => updateParticipant(index, 'phone', e.target.value)}
                            placeholder="+33 6 12 34 56 78"
                            data-testid={`participant-${index}-phone`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {(() => {
                    const slotInfo = selectedSlot ? availability.find(a => a.time_slot === selectedSlot) : null;
                    const maxAllowed = slotInfo ? Math.min(trip.max_participants, slotInfo.available_spots) : trip.max_participants;
                    return participants.length < maxAllowed && (
                      <Button
                        variant="outline"
                        onClick={addParticipant}
                        className="w-full rounded-full border-2 border-dashed border-[#b7ede0] bg-[#b7ede0]/10 text-[#000000] hover:bg-[#b7ede0] hover:border-[#b7ede0] py-6 text-base font-semibold transition-all duration-200"
                        data-testid="add-participant-btn"
                      >
                        <Plus size={22} weight="bold" className="mr-2" />
                        {t('booking.addParticipant')}
                      </Button>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Contact Info */}
            {selectedSlot && (
              <Card className="animate-fade-up" id="customer-info-section">
                <CardHeader>
                  <CardTitle className="text-xl font-['Playfair_Display']">Contact</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('booking.email')} *</Label>
                      <Input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="email@example.com"
                        data-testid="customer-email"
                      />
                    </div>
                    <div>
                      <Label>{t('booking.phone')} *</Label>
                      <Input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="+33 6 12 34 56 78"
                        data-testid="customer-phone"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Sticky Summary */}
          <div className="lg:col-span-2">
            <div className="sticky top-28">
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-xl font-['Playfair_Display']">{t('booking.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Summary */}
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4B5563]">Sortie</span>
                      <span className="font-medium text-[#000000]">{getLocalizedField(trip, 'name')}</span>
                    </div>
                    {selectedDuration && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#4B5563]">
                          {language === 'fr' ? 'Durée' : language === 'it' ? 'Durata' : 'Duration'}
                        </span>
                        <span className="font-medium text-[#000000]">{selectedDuration.label}</span>
                      </div>
                    )}
                    {selectedDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#4B5563]">Date</span>
                        <span className="font-medium text-[#000000]">
                          {format(selectedDate, 'dd MMMM yyyy', { locale: locales[language] })}
                        </span>
                      </div>
                    )}
                    {selectedSlot && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#4B5563]">Horaire</span>
                        <span className="font-medium text-[#000000]">{selectedSlot}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4B5563]">
                        {isPrivatization ? t('trips.privatization') : `${participants.length} participant(s)`}
                      </span>
                      <span className="font-medium text-[#000000]">
                        {calculateTotal()}€
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-[#E2E8F0] pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-[#000000]">{t('booking.total')}</span>
                      <span className="text-3xl font-bold text-[#000000]">{calculateTotal()}€</span>
                    </div>
                  </div>

                  {/* Payment Form */}
                  {selectedSlot && (
                    <div className="space-y-4 animate-fade-up">
                      <div className="flex items-center justify-between">
                        <Label>Carte bancaire</Label>
                        <PaymentIcons size="small" />
                      </div>
                      <div 
                        id="card-container" 
                        className="border border-[#E2E8F0] rounded-lg p-4 min-h-[50px]"
                      />
                      {!squareLoaded && (
                        <div className="text-sm text-[#6B7280] flex items-center gap-2">
                          <span className="inline-block w-4 h-4 border-2 border-[#b7ede0] border-t-transparent rounded-full animate-spin" />
                          <span>Chargement du paiement sécurisé...</span>
                        </div>
                      )}
                      
                      <Button
                        onClick={handlePayment}
                        disabled={processing || !squareLoaded}
                        className="w-full bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full py-6 text-lg"
                        data-testid="pay-btn"
                      >
                        {processing ? (
                          <>
                            <div className="spinner w-5 h-5 mr-2" />
                            {t('booking.processing')}
                          </>
                        ) : (
                          <>
                            <Check size={20} className="mr-2" />
                            {t('booking.pay')} {calculateTotal()}€
                          </>
                        )}
                      </Button>
                      
                      <div className="flex items-center justify-center gap-2 pt-1">
                        <ShieldCheck size={16} className="text-[#10B981]" />
                        <span className="text-xs text-[#4B5563]">Paiement sécurisé par Square</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripDetailPage;
